import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/lib/format";
import {
  Search,
  Bell,
  Wifi,
  WifiOff,
  RefreshCw,
  LogOut,
  ChevronDown,
  Menu as MenuIcon,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { GlobalSearch } from "@/components/GlobalSearch";


const SyncPill = ({ status }) => {
  const map = {
    online: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      label: "Online",
      dot: "bg-emerald-500",
    },

    syncing: {
      cls: "bg-amber-50 text-amber-700 border-amber-200",
      label: "Syncing",
      dot: "bg-amber-500",
    },

    offline: {
      cls: "bg-red-50 text-red-700 border-red-200",
      label: "Offline",
      dot: "bg-red-500",
    },
  };

  const s = map[status] || map.online;

  return (
    <div
      className={cn(
        "hidden sm:flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold",
        s.cls,
      )}
      data-testid="sync-status"
    >
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          s.dot,
          status === "syncing" && "animate-pulse",
        )}
      />

      {s.label}
    </div>
  );
};


export const Topbar = ({ onMobileNav }) => {
  const {
    restaurant,
    branches,
    branchId,
    changeBranch,
    syncStatus,
    subscribe,
  } = useApp();

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [notifs, setNotifs] = useState([]);
  const [now, setNow] = useState(new Date());
  const [searchOpen, setSearchOpen] = useState(false);

  // ---------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------

  const loadNotifs = async () => {
    try {
      const response = await api.notifications(branchId);
      setNotifs(Array.isArray(response.data) ? response.data : []);
    } catch {
      setNotifs([]);
    }
  };

  useEffect(() => {
    loadNotifs();
  }, [branchId]);

  useEffect(() => {
    const unsubscribe = subscribe(
      "notification",
      () => {
        loadNotifs();
      },
    );

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [subscribe, branchId]);


  // ---------------------------------------------------------
  // Clock
  // ---------------------------------------------------------

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => clearInterval(timer);
  }, []);


  // ---------------------------------------------------------
  // Global Search Shortcut
  // ---------------------------------------------------------

  useEffect(() => {
    const onKey = (e) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key.toLowerCase() === "k"
      ) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);


  // ---------------------------------------------------------
  // Notification click
  // ---------------------------------------------------------

  const handleNotificationClick = async (notification) => {
    /*
     * New notifications should contain order_id.
     *
     * For older notifications, we fallback to extracting
     * the order number from the notification message.
     */

    let orderId = notification?.order_id;

    if (!orderId) {
      const text = `${notification?.title || ""} ${
        notification?.message || ""
      }`;

      const match = text.match(/#(\d+)/);

      if (match?.[1]) {
        const orderNo = Number(match[1]);

        try {
          const response = await api.orders({
            branch_id: notification?.branch_id || branchId,
            limit: 200,
          });

          const found = (response.data || []).find(
            (order) => Number(order.order_no) === orderNo,
          );

          orderId = found?.id;
        } catch {
          // Fallback to orders page below.
        }
      }
    }

    if (orderId) {
      navigate(
        `/app/orders?orderId=${encodeURIComponent(orderId)}`,
      );
    } else {
      navigate("/app/orders");
    }
  };


  // ---------------------------------------------------------
  // Sync status
  // ---------------------------------------------------------

  const effectiveSyncStatus =
    typeof navigator !== "undefined" && !navigator.onLine
      ? "offline"
      : syncStatus || "online";


  return (
    <>
      <header
        className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur-xl border-b border-border flex items-center gap-3 px-4 sm:px-6"
        data-testid="topbar"
      >

        {/* Mobile navigation */}
        <button
          className="md:hidden text-foreground"
          onClick={onMobileNav}
          data-testid="mobile-nav-btn"
          type="button"
        >
          <MenuIcon size={22} />
        </button>


        {/* Logo + Branch */}
        <div className="flex items-center gap-3">

          <img
            src="/assets/logo.png"
            alt="StayServe"
            className="h-9 w-auto max-w-[140px] object-contain"
          />

          <Select
            value={branchId}
            onValueChange={changeBranch}
          >
            <SelectTrigger
              className="h-9 w-[150px] rounded-xl border-border bg-white text-sm"
              data-testid="branch-selector"
            >
              <SelectValue placeholder="Branch" />
            </SelectTrigger>

            <SelectContent className="bg-white">

              <SelectItem value="all">
                All Branches
              </SelectItem>

              {branches.map((branch) => (
                <SelectItem
                  key={branch.id}
                  value={branch.id}
                >
                  {branch.name}
                </SelectItem>
              ))}

            </SelectContent>
          </Select>

        </div>


        {/* Global Search */}
        <button
          onClick={() => setSearchOpen(true)}
          data-testid="global-search-btn"
          type="button"
          className="hidden lg:flex items-center gap-2 flex-1 max-w-sm h-9 rounded-xl border border-border bg-secondary/60 px-3 text-sm text-muted-foreground hover:bg-secondary transition-colors"
        >
          <Search size={16} />

          <span className="truncate">
            Search orders, items, customers…
          </span>

          <kbd className="ml-auto text-[10px] font-semibold bg-white border border-border rounded px-1.5 py-0.5">
            ⌘K
          </kbd>
        </button>


        <div className="ml-auto flex items-center gap-2 sm:gap-3">

          {/* Sync */}
          <SyncPill status={effectiveSyncStatus} />


          {/* Date / Time */}
          <div className="hidden xl:block text-right leading-tight">

            <div className="text-xs font-semibold text-foreground">
              {now.toLocaleDateString("en-IN", {
                weekday: "short",
                day: "numeric",
                month: "short",
              })}
            </div>

            <div className="text-[11px] text-muted-foreground">
              {now.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>

          </div>


          {/* Notifications */}
          <DropdownMenu>

            <DropdownMenuTrigger asChild>
              <button
                className="relative h-9 w-9 rounded-xl border border-border bg-white grid place-items-center text-foreground hover:bg-secondary transition-colors"
                data-testid="notifications-btn"
                type="button"
              >
                <Bell size={18} />

                {notifs.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold grid place-items-center">
                    {notifs.length > 9
                      ? "9+"
                      : notifs.length}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>


            <DropdownMenuContent
              align="end"
              className="w-80 bg-white"
            >

              <DropdownMenuLabel className="font-display">
                Notifications
              </DropdownMenuLabel>

              <DropdownMenuSeparator />


              <div className="max-h-80 overflow-y-auto">

                {notifs.length === 0 && (
                  <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                    No notifications
                  </div>
                )}


                {notifs.map((notification) => (
                  <DropdownMenuItem
                    key={notification.id}
                    onSelect={() =>
                      handleNotificationClick(notification)
                    }
                    className="px-3 py-2.5 cursor-pointer focus:bg-secondary/60"
                  >

                    <div className="w-full">

                      <div className="flex items-center justify-between gap-3">

                        <p className="text-sm font-semibold text-foreground truncate">
                          {notification.title}
                        </p>

                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {timeAgo(
                            notification.created_at,
                          )}
                        </span>

                      </div>

                      <p className="text-xs text-muted-foreground mt-0.5">
                        {notification.message}
                      </p>

                    </div>

                  </DropdownMenuItem>
                ))}

              </div>

            </DropdownMenuContent>

          </DropdownMenu>


          {/* Profile */}
          <DropdownMenu>

            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-xl border border-border bg-white pl-1 pr-2 py-1 hover:bg-secondary transition-colors"
                data-testid="profile-menu"
                type="button"
              >

                <span className="h-7 w-7 rounded-lg bg-primary text-white grid place-items-center text-xs font-bold">
                  {(user?.name || "U").charAt(0)}
                </span>

                <span className="hidden lg:block text-left leading-tight">

                  <span className="block text-xs font-semibold text-foreground">
                    {user?.name}
                  </span>

                  <span className="block text-[10px] text-muted-foreground capitalize">
                    {user?.role?.replace("_", " ")}
                  </span>

                </span>

                <ChevronDown
                  size={14}
                  className="text-muted-foreground"
                />

              </button>
            </DropdownMenuTrigger>


            <DropdownMenuContent
              align="end"
              className="w-48 bg-white"
            >

              <DropdownMenuLabel>
                {user?.email}
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onSelect={() => navigate("/app/settings")}
                data-testid="menu-settings"
              >
                Settings
              </DropdownMenuItem>

              <DropdownMenuItem
                onSelect={logout}
                className="text-red-600"
                data-testid="logout-btn"
              >
                <LogOut
                  size={15}
                  className="mr-2"
                />
                Logout
              </DropdownMenuItem>

            </DropdownMenuContent>

          </DropdownMenu>

        </div>

      </header>


      {/* Global Search Dialog */}
      <GlobalSearch
        open={searchOpen}
        setOpen={setSearchOpen}
      />

    </>
  );
};